import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import { getProject, getProjects } from '../../api/project.service.js';
import { getDepartments } from '../../api/department.service.js';
import { createTicket } from '../../api/ticket.service.js';
import { Select, SelectItem } from '../Select.jsx';
import {
  ticketInputClass,
  ticketPrimaryButtonClass,
  ticketSecondaryButtonClass,
  TICKET_TYPE_LABELS,
  TICKET_PRIORITY_OPTIONS,
} from './ticketUi.js';
import { getApiErrorMessage } from '../../lib/apiErrors.js';
import useUIStore from '../../store/useUIStore.js';

const EMPTY_FORM = {
  title: '',
  description: '',
  type: 'ISSUE',
  priority: 'MEDIUM',
  dueAt: '',
  referenceUrl: '',
  projectId: '',
  departmentId: '',
  moduleId: '',
  clientName: '',
  dealValue: '',
  currency: 'USD',
  targetQuarter: '',
  requestDetails: '',
  assetTag: '',
};

/**
 * Builds the type-specific `metadata` blob. Returns undefined for the classic
 * types, which carry no extra fields.
 */
const buildMetadata = (form) => {
  switch (form.type) {
    case 'LEAD':
      return {
        clientName: form.clientName || undefined,
        dealValue: form.dealValue ? Number(form.dealValue) : undefined,
        currency: form.currency || 'USD',
      };
    case 'PLAN':
      return { targetQuarter: form.targetQuarter || undefined };
    case 'IT_SERVICE':
      return {
        requestDetails: form.requestDetails || undefined,
        assetTag: form.assetTag || undefined,
      };
    default:
      return undefined;
  }
};

/**
 * The one ticket creation form.
 *
 * Whichever of project / department is passed in is fixed and shown read-only;
 * the other is rendered as a picker. Pass neither (the Tickets dashboard) and
 * both are pickers.
 *
 * @param {object}  props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string}  [props.projectId]     – fixes the ticket to this project
 * @param {string}  [props.departmentId]  – fixes the ticket to this department
 */
export default function TicketFormModal({ isOpen, onClose, projectId, departmentId }) {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  const [form, setForm] = useState(EMPTY_FORM);
  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  // Reset whenever the modal is (re)opened for a different context.
  useEffect(() => {
    if (isOpen) setForm(EMPTY_FORM);
  }, [isOpen, projectId, departmentId]);

  // The project whose modules we offer: the fixed one, or the one just picked.
  const activeProjectId = projectId || form.projectId;

  const { data: project } = useQuery({
    queryKey: ['project', activeProjectId, 'ticket-compose'],
    queryFn: async () => {
      const { data } = await getProject(activeProjectId);
      return data.data;
    },
    enabled: isOpen && !!activeProjectId,
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data } = await getProjects();
      return data.data;
    },
    enabled: isOpen && !projectId,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data } = await getDepartments();
      return data.data;
    },
    enabled: isOpen,
  });

  // Stable identity: this feeds a useEffect dependency list.
  const modules = useMemo(() => project?.modules ?? [], [project]);

  // Switching project invalidates a module chosen from the previous one.
  useEffect(() => {
    if (!form.moduleId) return;
    if (!modules.some((module) => module.id === form.moduleId)) {
      setField('moduleId', '');
    }
  }, [modules, form.moduleId]);

  const createMutation = useMutation({
    mutationFn: (payload) => createTicket(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['project-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['department-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['department-dashboard'] });
      addToast({ type: 'success', message: 'Ticket created successfully.' });
      onClose();
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    createMutation.mutate({
      title: form.title,
      description: form.description,
      type: form.type,
      priority: form.priority,
      dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
      referenceUrl: form.referenceUrl.trim() || undefined,
      projectId: projectId || form.projectId || undefined,
      departmentId: departmentId || form.departmentId || undefined,
      moduleId: form.moduleId || undefined,
      metadata: buildMetadata(form),
    });
  };

  if (!isOpen) return null;

  const fixedDepartmentName = departmentId
    ? departments.find((department) => department.id === departmentId)?.name
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl rounded-3xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/40 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">Create ticket</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Route the request into a department first. The department head will assign
              ownership after triage.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-card hover:text-foreground cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-card max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-foreground">Title</label>
              <input
                required
                value={form.title}
                onChange={(event) => setField('title', event.target.value)}
                className={ticketInputClass}
                placeholder="Summarize the issue, request, or change"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                required
                rows={4}
                value={form.description}
                onChange={(event) => setField('description', event.target.value)}
                className={ticketInputClass}
                placeholder="Describe the user impact, expected outcome, and any context the department should see during triage"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Type</label>
              <Select
                value={form.type}
                onChange={(event) => setField('type', event.target.value)}
                className={ticketInputClass}
              >
                {Object.entries(TICKET_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Priority</label>
              <Select
                value={form.priority}
                onChange={(event) => setField('priority', event.target.value)}
                className={ticketInputClass}
              >
                {TICKET_PRIORITY_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Due date <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={form.dueAt}
                onChange={(event) => setField('dueAt', event.target.value)}
                className={ticketInputClass}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Reference link <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={form.referenceUrl}
                onChange={(event) => setField('referenceUrl', event.target.value)}
                className={ticketInputClass}
                placeholder="https://sharepoint.../spec.docx"
              />
            </div>

            {form.type === 'LEAD' && (
              <>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Client name
                  </label>
                  <input
                    type="text"
                    value={form.clientName}
                    onChange={(event) => setField('clientName', event.target.value)}
                    className={ticketInputClass}
                    placeholder="Client company or person"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Deal value ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.dealValue}
                    onChange={(event) => setField('dealValue', event.target.value)}
                    className={ticketInputClass}
                    placeholder="Value in USD"
                  />
                </div>
              </>
            )}

            {form.type === 'PLAN' && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Target quarter
                </label>
                <input
                  type="text"
                  value={form.targetQuarter}
                  onChange={(event) => setField('targetQuarter', event.target.value)}
                  className={ticketInputClass}
                  placeholder="e.g. Q3 2026"
                  pattern="Q[1-4] \d{4}"
                  title="Format: Q1-4 followed by space and 4-digit year, e.g. Q3 2026"
                />
              </div>
            )}

            {form.type === 'IT_SERVICE' && (
              <>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Request details
                  </label>
                  <input
                    type="text"
                    value={form.requestDetails}
                    onChange={(event) => setField('requestDetails', event.target.value)}
                    className={ticketInputClass}
                    placeholder="Details of the request"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Asset tag
                  </label>
                  <input
                    type="text"
                    value={form.assetTag}
                    onChange={(event) => setField('assetTag', event.target.value)}
                    className={ticketInputClass}
                    placeholder="Asset ID / identifier"
                  />
                </div>
              </>
            )}

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Project</label>
              {projectId ? (
                <div className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground">
                  {project?.name || 'Loading project details...'}
                </div>
              ) : (
                <Select
                  value={form.projectId}
                  onChange={(event) => setField('projectId', event.target.value)}
                  className={ticketInputClass}
                >
                  <SelectItem value="">No project selected</SelectItem>
                  {projects.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Department</label>
              {departmentId ? (
                <div className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground">
                  {fixedDepartmentName || 'Loading department...'}
                </div>
              ) : (
                <Select
                  value={form.departmentId}
                  onChange={(event) => setField('departmentId', event.target.value)}
                  className={ticketInputClass}
                >
                  <SelectItem value="">Select a department</SelectItem>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-foreground">Module</label>
              <Select
                value={form.moduleId}
                onChange={(event) => setField('moduleId', event.target.value)}
                className={ticketInputClass}
                disabled={!activeProjectId}
              >
                <SelectItem value="">
                  {activeProjectId ? 'No module selected' : 'Pick a project first'}
                </SelectItem>
                {modules.map((module) => (
                  <SelectItem key={module.id} value={module.id}>
                    {module.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>

          {createMutation.error && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {getApiErrorMessage(createMutation.error, 'Unable to create the ticket.')}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end border-t border-border pt-4">
            <button type="button" onClick={onClose} className={ticketSecondaryButtonClass}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className={ticketPrimaryButtonClass}
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Creating...' : 'Create ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
